"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";

const contactMethods = [
  {
    icon: "EnvelopeIcon",
    title: "Email Us",
    description: "Send us an email anytime",
    value: "support@petfob.com",
    link: "mailto:support@petfob.com",
    color: "#0EA5A0",
    bg: "#E0F7F6",
  },
  {
    icon: "ClockIcon",
    title: "Support Hours",
    description: "We\'re available to help",
    value: "Mon-Fri: 9am-6pm EST",
    link: null,
    color: "#7C3AED",
    bg: "#EDE9FE",
  },
  {
    icon: "ChatBubbleLeftRightIcon",
    title: "Live Chat",
    description: "Chat with our team",
    value: "Coming Soon",
    link: null,
    color: "#0369A1",
    bg: "#E0F2FE",
  },
];

const quickLinks = [
  { label: "How It Works", href: "/how-it-works", icon: "QuestionMarkCircleIcon" },
  { label: "For Pet Owners", href: "/for-pet-owners", icon: "UserIcon" },
  { label: "For Businesses", href: "/for-businesses", icon: "BriefcaseIcon" },
  { label: "Privacy Policy", href: "#", icon: "ShieldCheckIcon" },
];

export default function ContactInfo() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target
              .querySelectorAll(".scroll-fade-right")
              .forEach((el, i) => {
                setTimeout(() => el.classList.add("is-visible"), i * 90);
              });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-16 px-5 sm:px-8 lg:px-12"
      style={{ background: "linear-gradient(160deg, #F0FAFB 0%, #F7F8F6 100%)" }}
    >
      <div className="max-w-xl mx-auto lg:mx-0 scroll-fade-right">
        <h2 className="text-3xl font-bold text-[#1A2B2B] font-heading mb-3">
          Other ways to reach us
        </h2>
        <p className="text-[#4A6363] leading-relaxed mb-8">
          Choose the method that works best for you. We're committed to responding quickly.
        </p>

        {/* Contact Methods */}
        <div className="flex flex-col gap-4 mb-10">
          {contactMethods.map((method, idx) => (
            <div key={idx} className="feature-card p-5 flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: method.bg }}
              >
                <Icon
                  name={method.icon as "EnvelopeIcon"}
                  size={20}
                  style={{ color: method.color }}
                />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-[#1A2B2B] font-heading mb-1">
                  {method.title}
                </h4>
                <p className="text-xs text-[#8AA4A4] mb-2">{method.description}</p>
                {method.link ? (
                  <a
                    href={method.link}
                    className="text-sm font-semibold hover:underline"
                    style={{ color: method.color }}
                  >
                    {method.value}
                  </a>
                ) : (
                  <p className="text-sm font-semibold text-[#4A6363]">{method.value}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Quick Links */}
        <div className="border-t border-[#E2EDED] pt-8">
          <h3 className="text-lg font-bold text-[#1A2B2B] font-heading mb-4">
            Helpful Resources
          </h3>
          <div className="flex flex-col gap-2">
            {quickLinks.map((link, idx) => (
              <Link
                key={idx}
                href={link.href}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white transition-colors duration-200 group"
              >
                <Icon
                  name={link.icon as "QuestionMarkCircleIcon"}
                  size={18}
                  className="text-teal-500 group-hover:scale-110 transition-transform"
                />
                <span className="text-sm font-medium text-[#4A6363] group-hover:text-teal-600">
                  {link.label}
                </span>
                <Icon
                  name="ChevronRightIcon"
                  size={14}
                  className="text-[#8AA4A4] ml-auto"
                />
              </Link>
            ))}
          </div>
        </div>

        {/* Support Note */}
        <div className="mt-8 p-5 bg-white rounded-2xl border border-[#E2EDED]">
          <div className="flex items-start gap-3">
            <Icon name="InformationCircleIcon" size={20} className="text-teal-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-bold text-[#1A2B2B] font-heading mb-1">
                Need urgent help?
              </h4>
              <p className="text-xs text-[#4A6363] leading-relaxed">
                For time-sensitive issues related to lost pets or emergency access, please email us directly at{" "}
                <a href="mailto:urgent@petfob.com" className="text-teal-600 font-semibold hover:underline">
                  urgent@petfob.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}